/**
 * Sprint 4 A3: Sentiment Detection Badge
 * Green/amber/red indicator based on conversation sentiment score.
 * Hover shows "AI sentiment analysis" per EU AI Act.
 */

interface SentimentBadgeProps {
  score: number | null; // 0-1 scale. null = not analyzed yet.
  compact?: boolean;
}

const SENTIMENT_CONFIG = {
  positive: { color: "bg-[var(--color-success)]/50", label: "Positive", threshold: 0.6 },
  neutral: { color: "bg-[var(--color-warning)]/40", label: "Neutral", threshold: 0.3 },
  negative: { color: "bg-[var(--color-destructive)]/50", label: "Negative", threshold: 0 },
} as const;

function getSentiment(score: number | null): typeof SENTIMENT_CONFIG[keyof typeof SENTIMENT_CONFIG] {
  if (score === null) return SENTIMENT_CONFIG.neutral;
  if (score >= SENTIMENT_CONFIG.positive.threshold) return SENTIMENT_CONFIG.positive;
  if (score >= SENTIMENT_CONFIG.neutral.threshold) return SENTIMENT_CONFIG.neutral;
  return SENTIMENT_CONFIG.negative;
}

export function SentimentBadge({ score, compact = false }: SentimentBadgeProps) {
  const sentiment = getSentiment(score);

  if (compact) {
    return (
      <span
        className={`inline-block w-2 h-2 rounded-full ${sentiment.color}`}
        title={`AI sentiment analysis: ${sentiment.label} (${score !== null ? Math.round(score * 100) : "N/A"}%)`}
      />
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded-full ${sentiment.color} text-white`}
      title="AI sentiment analysis"
    >
      {sentiment.label}
    </span>
  );
}

